import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const AZURE_CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID;
const AZURE_TENANT_ID = process.env.REACT_APP_AZURE_TENANT_ID;

// Finding 4: Send cookies on every axios request (HttpOnly cookie auth)
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle OAuth callback — check URL for code + state after redirect back
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state'); // 'google' or 'microsoft'

    // Skip if this is the SharePoint OAuth callback — handled by SharePointCallback page
    if (code && state && !window.location.pathname.startsWith('/sharepoint-callback')) {
      // Clean URL immediately
      window.history.replaceState({}, '', '/');

      const exchangeCode = async () => {
        try {
          // Must match the redirect_uri used in the authorization request
          const callbackPath = state === 'google'
            ? '/api/auth/callback/google'
            : '/api/auth/callback/azure-ad';
          const redirectUri = `${window.location.origin}${callbackPath}`;
          // Finding 4: withCredentials so the server can set HttpOnly cookie
          const response = await axios.post(`${API}/auth/oauth/exchange`, {
            provider: state,
            code,
            redirect_uri: redirectUri,
          });
          const { user: userData } = response.data;
          setUser(userData);
          toast.success('Welcome back!');
          // Force full page navigation to /dashboard
          window.location.replace('/dashboard');
        } catch (error) {
          const msg = error.response?.data?.detail || 'OAuth login failed';
          toast.error(msg);
        } finally {
          setLoading(false);
        }
      };

      exchangeCode();
      return; // skip normal session check — exchangeCode handles it
    }

    const authError = params.get('auth_error');
    if (authError) {
      toast.error(decodeURIComponent(authError));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Finding 4: Verify session via /auth/me — token is in HttpOnly cookie, not localStorage
  const verifySession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip session verify only when the first useEffect is actively handling an OAuth exchange
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const isSharePointCallback = window.location.pathname.startsWith('/sharepoint-callback');
    const isOAuthExchange = code && state && !isSharePointCallback;

    if (!isOAuthExchange) {
      verifySession();
    }
  }, [verifySession]);

  const signInWithEmail = async (email, password, captchaToken) => {
    // Finding 4: Server sets HttpOnly cookie; we only read user data from body
    const response = await axios.post(`${API}/auth/login`, { email, password, captcha_token: captchaToken });
    const { user: userData } = response.data;
    setUser(userData);
    return response.data;
  };

  const signInWithGoogle = () => {
    const redirectUri = `${window.location.origin}/api/auth/callback/google`;
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'select_account',
      state: 'google',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const signInWithMicrosoft = () => {
    const redirectUri = `${window.location.origin}/api/auth/callback/azure-ad`;
    const params = new URLSearchParams({
      client_id: AZURE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile User.Read',
      response_mode: 'query',
      prompt: 'select_account',
      state: 'microsoft',
    });
    window.location.href = `https://login.microsoftonline.com/${AZURE_TENANT_ID}/oauth2/v2.0/authorize?${params}`;
  };

  // Finding 2: Call logout API to blocklist the token, then clear local state
  const signOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Silent — still clear local state even if request fails
    }
    setUser(null);
    toast.success('Logged out successfully');
    window.location.replace('/');
  };

  const value = {
    user,
    loading,
    signInWithEmail,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
